import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Save,
  CheckCircle2,
  X as XIcon,
  Plus,
  Trash2,
  Edit,
  AlertCircle,
  Upload,
  Camera,
} from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Safety inspection checklist items (from PDF)
const INSPECTION_ITEMS = [
  // Column 1
  { id: "mobile_plant_separation", hazard: "MOBILE PLANT Separation of plant/people" },
  { id: "mobile_plant_inspection", hazard: "MOBILE PLANT Daily inspection completed" },
  { id: "mobile_plant_inducted", hazard: "MOBILE PLANT - Inducted" },
  { id: "excavations_inspected", hazard: "EXCAVATIONS Inspected" },
  { id: "first_aid_facilities", hazard: "FIRST AID FACILITIES IN PLACE" },
  { id: "exclusion_zones", hazard: "EXCLUSION ZONES INPLACE" },
  { id: "scaffolding", hazard: "SCAFFOLDING: Erected to Manufact. Specs/Tagged" },
  { id: "formwork_support", hazard: "FORMWORK SUPPORT inspected" },
  { id: "house_keeping", hazard: "HOUSE KEEPING high quality" },
  { id: "fire_extinguishers", hazard: "FIRE EXTINGUISHERS checked" },
  { id: "evacuation_siren", hazard: "EVACUATION SIREN tested" },
  { id: "temporary_structures", hazard: "TEMPORARY STRUCTURES INSPECTED" },
  { id: "ladders", hazard: "LADDERS - Safe use of" },
  
  // Column 2
  { id: "fall_protection", hazard: "FALL PROTECTION in place" },
  { id: "public_protection", hazard: "PUBLIC PROTECTION in place" },
  { id: "falling_object_prevention", hazard: "FALLING OBJECT PREVENTION in place" },
  { id: "swms_for_hrcw", hazard: "SWMS'S FOR ALL HRCW" },
  { id: "swms_followed", hazard: "SWMS's followed" },
  { id: "manual_handling", hazard: "MANUAL HANDLING compliant" },
  { id: "traffic_management", hazard: "TRAFFIC MANAGEMENT compliant" },
  { id: "site_security", hazard: "SITE SECURITY in place" },
  { id: "props_locked", hazard: "PROPS LOCKED/PROTECTED" },
  { id: "electrical_safety", hazard: "ELECTRICAL SAFETY checked" },
  { id: "adequate_power", hazard: "ADEQUATE POWER ON SITE" },
  { id: "penos_covered", hazard: "PENO'S COVERED/SECURED" },
  
  // Column 3
  { id: "electrical_test_tag", hazard: "ELECTRICAL TEST/TAG in date" },
  { id: "hoarding_fencing", hazard: "HOARDING/FENCING Good condition" },
  { id: "public_footpaths", hazard: "PUBLIC FOOTPATHS IN good condition" },
  { id: "access_egress", hazard: "ACCESS/EGRESS clear" },
  { id: "environmental_issues", hazard: "ENVIRONMENTAL ISSUES" },
  { id: "stormwater_protected", hazard: "STORMWATER PROTECTED" },
  { id: "waste_management", hazard: "WASTE MANAGEMENT in place" },
  { id: "asbestos_managed", hazard: "ASBESTOS managed" },
  { id: "noise_vibration", hazard: "NOISE / VIBRATION MANAGED" },
  { id: "dust_suppression", hazard: "DUST SUPPRESSION IN PLACE" },
  { id: "stockpiles_covered", hazard: "STOCKPILES COVERED ETC" },
  { id: "spill_kit", hazard: "SPILL KIT/S AVAILABLE" },
  { id: "mdf_cutting_room", hazard: "MDF CUTTING ROOM" },
];

const PRIORITY_OPTIONS = [
  { value: "A", label: "A: Immediate Action", color: "bg-red-600" },
  { value: "B", label: "B: Within 24 hours", color: "bg-orange-600" },
  { value: "C", label: "C: Within 1 week", color: "bg-yellow-600" },
  { value: "D", label: "D: Ongoing", color: "bg-blue-600" },
];

// Helper function to get local date in YYYY-MM-DD format
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function SafetyWalkForm() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { walkId } = useParams();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAttendeeDialog, setShowAttendeeDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null); // ID of action being edited
  const [signatureDialogFor, setSignatureDialogFor] = useState(null); // attendee index
  const sigCanvas = useRef(null);
  
  // Check if user is view_only (read-only mode)
  const isViewOnly = user?.role === "view_only";

  const token = localStorage.getItem("token");
  const isEditMode = !!walkId;

  // Form state
  const [formData, setFormData] = useState({
    site_id: "",
    project_name: "",
    location: "",
    date: getLocalDateString(),
    time: new Date().toTimeString().slice(0, 5),
    attendees: [],
    inspection_items: INSPECTION_ITEMS.map(item => ({ hazard: item.hazard, status: "compliant" })),
    actions: [],
    status: "draft"
  });

  // New attendee state
  const [newAttendee, setNewAttendee] = useState({
    name: "",
    company: "",
    signature: "",
    signed_at: ""
  });

  // New action state
  const [newAction, setNewAction] = useState({
    hazard_action: "",
    location: "",
    priority: "B",
    who_responsible: "",
    date_to_rectify: getLocalDateString(),
    closed_out_date: "",
    closed_out_by: "",
    status: "open",
    images: []
  });

  // Camera capture states
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Load sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await fetch(`${API}/sites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSites(data);
        }
      } catch (error) {
        console.error("Error loading sites:", error);
      }
    };

    loadSites();
  }, [token]);

  // Load safety walk if editing
  useEffect(() => {
    if (isEditMode) {
      loadSafetyWalk();
    }
  }, [walkId]);

  const loadSafetyWalk = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/safety-walks/${walkId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
      } else {
        toast.error("Failed to load safety walk");
        navigate("/ohs/safety-walks");
      }
    } catch (error) {
      console.error("Error loading safety walk:", error);
      toast.error("Failed to load safety walk");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (completeWalk = false) => {
    // Validation
    if (!formData.site_id || !formData.project_name || !formData.location) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.attendees.length === 0) {
      toast.error("Please add at least one attendee");
      return;
    }

    // Check for open actions when trying to complete
    if (completeWalk) {
      const openActions = formData.actions.filter(a => a.status === "open");
      if (openActions.length > 0) {
        toast.error(
          `Cannot complete safety walk. ${openActions.length} action${openActions.length > 1 ? 's' : ''} still open. Please close all actions before completing.`,
          { duration: 5000 }
        );
        return;
      }
    }

    const dataToSave = {
      ...formData,
      status: completeWalk ? "completed" : "draft"
    };

    setLoading(true);
    try {
      const url = isEditMode 
        ? `${API}/safety-walks/${walkId}`
        : `${API}/safety-walks`;
      
      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSave),
      });

      if (response.ok) {
        toast.success(completeWalk ? "Safety walk completed!" : "Safety walk saved as open walk");
        navigate("/ohs/safety-walks");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save safety walk");
      }
    } catch (error) {
      console.error("Error saving safety walk:", error);
      toast.error("Failed to save safety walk");
    } finally {
      setLoading(false);
    }
  };

  const handleInspectionChange = (index, status) => {
    const newItems = [...formData.inspection_items];
    newItems[index].status = status;
    setFormData({ ...formData, inspection_items: newItems });
  };

  const handleAddAttendee = () => {
    if (!newAttendee.name || !newAttendee.company) {
      toast.error("Please enter name and company");
      return;
    }

    setFormData({
      ...formData,
      attendees: [...formData.attendees, { ...newAttendee }]
    });

    setNewAttendee({ name: "", company: "", signature: "", signed_at: "" });
    setShowAttendeeDialog(false);
    toast.success("Attendee added");
  };

  const handleRemoveAttendee = (index) => {
    const newAttendees = formData.attendees.filter((_, i) => i !== index);
    setFormData({ ...formData, attendees: newAttendees });
    toast.success("Attendee removed");
  };

  const handleAddSignature = (attendeeIndex) => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      const newAttendees = [...formData.attendees];
      newAttendees[attendeeIndex].signature = signatureData;
      newAttendees[attendeeIndex].signed_at = new Date().toISOString();
      setFormData({ ...formData, attendees: newAttendees });
      setSignatureDialogFor(null);
      sigCanvas.current.clear();
      toast.success("Signature added");
    } else {
      toast.error("Please provide a signature");
    }
  };

  // Handle action image upload
  const handleActionImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const currentImages = newAction.images || [];
    
    if (currentImages.length + files.length > 3) {
      toast.error("Maximum 3 images allowed per action");
      return;
    }

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum 5MB per image.`);
        continue;
      }

      try {
        // Compress and convert to base64
        const compressedBase64 = await compressImage(file, 800, 0.7);
        setNewAction(prev => ({
          ...prev,
          images: [...(prev.images || []), compressedBase64]
        }));
      } catch (error) {
        console.error("Error processing image:", error);
        toast.error(`Failed to process ${file.name}`);
      }
    }
  };

  // Image compression helper
  const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleRemoveActionImage = (index) => {
    setNewAction(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
    toast.success("Image removed");
  };

  // Camera capture functions
  const startCamera = async () => {
    if (newAction.images?.length >= 3) {
      toast.error("Maximum 3 images allowed per action");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" }, // Use rear camera on mobile
        audio: false 
      });
      setCameraStream(stream);
      setShowCameraDialog(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Cannot access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraDialog(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    
    // Convert to base64 with compression
    const imageData = canvas.toDataURL("image/jpeg", 0.7);
    
    // Add to images
    setNewAction(prev => ({
      ...prev,
      images: [...(prev.images || []), imageData]
    }));
    
    toast.success("Photo captured!");
    stopCamera();
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const handleSaveAction = () => {
    if (!newAction.hazard_action || !newAction.location || !newAction.who_responsible) {
      toast.error("Please fill in all action fields");
      return;
    }

    if (editingActionId) {
      // Update existing action
      const newActions = formData.actions.map(action => 
        action.id === editingActionId ? { ...newAction, id: editingActionId } : action
      );
      setFormData({ ...formData, actions: newActions });
      toast.success("Action updated");
    } else {
      // Add new action
      const actionWithId = {
        ...newAction,
        id: `action_${Date.now()}`
      };
      setFormData({
        ...formData,
        actions: [...formData.actions, actionWithId]
      });
      toast.success("Action added");
    }

    // Reset form
    setNewAction({
      hazard_action: "",
      location: "",
      priority: "B",
      who_responsible: "",
      date_to_rectify: getLocalDateString(),
      closed_out_date: "",
      closed_out_by: "",
      status: "open",
      images: []
    });
    setEditingActionId(null);
    setShowActionDialog(false);
  };

  const handleEditAction = (action) => {
    setNewAction(action);
    setEditingActionId(action.id);
    setShowActionDialog(true);
  };

  const handleRemoveAction = (actionId) => {
    const newActions = formData.actions.filter(a => a.id !== actionId);
    setFormData({ ...formData, actions: newActions });
    toast.success("Action removed");
  };

  const handleToggleActionStatus = (actionId) => {
    const newActions = formData.actions.map(action => {
      if (action.id === actionId) {
        const isClosed = action.status === "closed";
        return {
          ...action,
          status: isClosed ? "open" : "closed",
          closed_out_date: isClosed ? "" : new Date().toISOString().split('T')[0],
          closed_out_by: isClosed ? "" : user.name
        };
      }
      return action;
    });
    setFormData({ ...formData, actions: newActions });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? site.name : "";
  };

  if (loading && isEditMode) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-center">
        <p className="text-slate-600">Loading safety walk...</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
          {isEditMode ? "Edit Safety Walk" : "New Safety Walk"}
        </h1>
        <p className="text-sm sm:text-base text-slate-600">HSE 2.02 Safety Walk Inspection</p>
      </div>

      {/* Project Details */}
      <Card className="mb-4 sm:mb-6">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <Label htmlFor="site">Site *</Label>
              <Select
                value={formData.site_id}
                onValueChange={(value) => {
                  const site = sites.find(s => s.id === value);
                  setFormData({
                    ...formData,
                    site_id: value,
                    project_name: site ? site.name : "",
                    location: site ? site.address : ""
                  });
                }}
              >
                <SelectTrigger id="site">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="project_name">Project Name *</Label>
              <Input
                id="project_name"
                value={formData.project_name}
                onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="location">Location *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="date">Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="time">Time *</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Attendees */}
      <Card className="mb-4 sm:mb-6">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <CardTitle className="text-lg sm:text-xl">Attendees</CardTitle>
          {!isViewOnly && (
            <Button size="sm" onClick={() => setShowAttendeeDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Add Attendee</span>
              <span className="sm:hidden">Add</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {formData.attendees.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No attendees added yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Signature</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formData.attendees.map((attendee, index) => (
                  <TableRow key={index}>
                    <TableCell>{attendee.name}</TableCell>
                    <TableCell>{attendee.company}</TableCell>
                    <TableCell>
                      {attendee.signature ? (
                        <img src={attendee.signature} alt="Signature" className="h-12" />
                      ) : (
                        !isViewOnly && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSignatureDialogFor(index)}
                          >
                            Add Signature
                          </Button>
                        )
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!isViewOnly && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveAttendee(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Inspection Checklist */}
      <Card className="mb-4 sm:mb-6">
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Safety Inspection Checklist</CardTitle>
          <div className="text-xs sm:text-sm text-slate-600 mt-2">
            <p className="mb-2">Tick only items that are checked. All items requiring close out MUST be listed in Actions section.</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-green-600" /> = Compliant/No action
              </span>
              <span className="flex items-center gap-1">
                <XIcon className="w-4 h-4 text-red-600" /> = Noncompliant/Action required
              </span>
              <span className="flex items-center gap-1">
                <span className="text-slate-400">N/A</span> = Not applicable
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Split into 3 columns */}
            {[0, 1, 2].map(colIndex => {
              const itemsPerCol = Math.ceil(INSPECTION_ITEMS.length / 3);
              const startIdx = colIndex * itemsPerCol;
              const endIdx = startIdx + itemsPerCol;
              const colItems = INSPECTION_ITEMS.slice(startIdx, endIdx);

              return (
                <div key={colIndex} className="space-y-2">
                  {colItems.map((item, idx) => {
                    const actualIndex = startIdx + idx;
                    const status = formData.inspection_items[actualIndex]?.status || "";

                    return (
                      <div key={item.id} className="border rounded p-3 bg-slate-50">
                        <p className="text-sm font-medium mb-2">{item.hazard}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={status === "compliant" ? "default" : "outline"}
                            className={status === "compliant" ? "bg-green-600" : ""}
                            onClick={() => handleInspectionChange(actualIndex, "compliant")}
                            disabled={isViewOnly}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={status === "noncompliant" ? "default" : "outline"}
                            className={status === "noncompliant" ? "bg-red-600" : ""}
                            onClick={() => handleInspectionChange(actualIndex, "noncompliant")}
                            disabled={isViewOnly}
                          >
                            <XIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={status === "na" ? "default" : "outline"}
                            className={status === "na" ? "bg-slate-600" : ""}
                            onClick={() => handleInspectionChange(actualIndex, "na")}
                            disabled={isViewOnly}
                          >
                            N/A
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Actions Requiring Closeout */}
      <Card className="mb-4 sm:mb-6">
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <CardTitle className="text-lg sm:text-xl">Actions Requiring Closeout</CardTitle>
              {formData.actions.length > 0 && (
                <div className="flex gap-2">
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs">
                    {formData.actions.filter(a => a.status === "open").length} Open
                  </Badge>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">
                    {formData.actions.filter(a => a.status === "closed").length} Closed
                  </Badge>
                </div>
              )}
            </div>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">
              All noncompliant items MUST be listed here with closeout details
            </p>
          </div>
          {!isViewOnly && (
            <Button size="sm" onClick={() => setShowActionDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Add Action</span>
              <span className="sm:hidden">Add</span>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {formData.actions.length === 0 ? (
            <p className="text-center text-slate-500 py-4">No actions added yet</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hazard/Action</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Who Responsible</TableHead>
                    <TableHead>Date to Rectify</TableHead>
                    <TableHead>Photos</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.actions.map((action) => {
                    const priority = PRIORITY_OPTIONS.find(p => p.value === action.priority);
                    return (
                      <TableRow key={action.id}>
                        <TableCell className="max-w-xs">{action.hazard_action}</TableCell>
                        <TableCell>{action.location}</TableCell>
                        <TableCell>
                          <Badge className={priority?.color}>{priority?.label}</Badge>
                        </TableCell>
                        <TableCell>{action.who_responsible}</TableCell>
                        <TableCell>
                          {new Date(action.date_to_rectify).toLocaleDateString("en-AU")}
                        </TableCell>
                        <TableCell>
                          {action.images?.length > 0 ? (
                            <div className="flex items-center gap-1">
                              <Camera className="w-4 h-4 text-blue-600" />
                              <span className="text-sm">{action.images.length}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isViewOnly ? (
                            <Badge variant={action.status === "closed" ? "default" : "outline"} className={action.status === "closed" ? "bg-green-600" : ""}>
                              {action.status === "closed" ? "Closed" : "Open"}
                            </Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant={action.status === "closed" ? "default" : "outline"}
                              className={action.status === "closed" ? "bg-green-600" : ""}
                              onClick={() => handleToggleActionStatus(action.id)}
                            >
                              {action.status === "closed" ? (
                                <>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Closed
                                </>
                              ) : (
                                "Mark Closed"
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!isViewOnly && (
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditAction(action)}
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveAction(action.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-3 sm:space-y-4">
        {/* Warning message if open actions exist */}
        {formData.actions.filter(a => a.status === "open").length > 0 && (
          <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm sm:text-base text-orange-900">
                  {formData.actions.filter(a => a.status === "open").length} Open Action{formData.actions.filter(a => a.status === "open").length > 1 ? 's' : ''} Remaining
                </p>
                <p className="text-xs sm:text-sm text-orange-700">
                  All actions must be closed out before the safety walk can be marked as completed.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4">
          <Button
            variant="outline"
            onClick={() => navigate("/ohs/safety-walks")}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {isViewOnly ? "Back" : "Cancel"}
          </Button>
          {!isViewOnly && (
            <>
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                <Save className="w-4 h-4 mr-2" />
                Save as Open Walk
              </Button>
              <Button
                onClick={() => handleSave(true)}
                disabled={loading || formData.actions.filter(a => a.status === "open").length > 0}
                className={`w-full sm:w-auto ${formData.actions.filter(a => a.status === "open").length > 0 ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Complete Safety Walk</span>
                <span className="sm:hidden">Complete</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Add Attendee Dialog */}
      <Dialog open={showAttendeeDialog} onOpenChange={setShowAttendeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Attendee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="attendee_name">Name *</Label>
              <Input
                id="attendee_name"
                value={newAttendee.name}
                onChange={(e) => setNewAttendee({ ...newAttendee, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="attendee_company">Company *</Label>
              <Input
                id="attendee_company"
                value={newAttendee.company}
                onChange={(e) => setNewAttendee({ ...newAttendee, company: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddAttendee} className="flex-1">
                Add
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAttendeeDialog(false);
                  setNewAttendee({ name: "", company: "", signature: "", signed_at: "" });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signatureDialogFor !== null} onOpenChange={() => setSignatureDialogFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Signature</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border rounded p-2 bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: "w-full h-40 border rounded"
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => sigCanvas.current?.clear()}
                className="flex-1"
              >
                Clear
              </Button>
              <Button
                onClick={() => handleAddSignature(signatureDialogFor)}
                className="flex-1"
              >
                Save Signature
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={(open) => {
        if (!open) {
          setShowActionDialog(false);
          setEditingActionId(null);
          setNewAction({
            hazard_action: "",
            location: "",
            priority: "B",
            who_responsible: "",
            date_to_rectify: getLocalDateString(),
            closed_out_date: "",
            closed_out_by: "",
            status: "open",
            images: []
          });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingActionId ? "Edit Action" : "Add Action"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="action_hazard">Hazard/Action Description *</Label>
              <Input
                id="action_hazard"
                value={newAction.hazard_action}
                onChange={(e) => setNewAction({ ...newAction, hazard_action: e.target.value })}
                placeholder="Describe the hazard or action required"
              />
            </div>
            <div>
              <Label htmlFor="action_location">Location *</Label>
              <Input
                id="action_location"
                value={newAction.location}
                onChange={(e) => setNewAction({ ...newAction, location: e.target.value })}
                placeholder="Specific location of the hazard"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="action_priority">Priority *</Label>
                <Select
                  value={newAction.priority}
                  onValueChange={(value) => setNewAction({ ...newAction, priority: value })}
                >
                  <SelectTrigger id="action_priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="action_responsible">Who Responsible *</Label>
                <Input
                  id="action_responsible"
                  value={newAction.who_responsible}
                  onChange={(e) => setNewAction({ ...newAction, who_responsible: e.target.value })}
                  placeholder="Person/Company responsible"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="action_date">Date to be Rectified *</Label>
              <Input
                id="action_date"
                type="date"
                value={newAction.date_to_rectify}
                onChange={(e) => setNewAction({ ...newAction, date_to_rectify: e.target.value })}
              />
            </div>
            
            {/* Image Upload Section */}
            <div>
              <Label>Photos (Max 3)</Label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={startCamera}
                    disabled={newAction.images?.length >= 3}
                    className="flex items-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    Take Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('action-image-upload').click()}
                    disabled={newAction.images?.length >= 3}
                    className="flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Photo
                  </Button>
                  <input
                    id="action-image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleActionImageUpload}
                    className="hidden"
                  />
                  <span className="text-sm text-slate-500 self-center">
                    {newAction.images?.length || 0}/3 photos
                  </span>
                </div>
                
                {/* Image Preview */}
                {newAction.images?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {newAction.images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img}
                          alt={`Action ${index + 1}`}
                          className="w-full h-24 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveActionImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <XIcon className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSaveAction} className="flex-1">
                {editingActionId ? "Update Action" : "Add Action"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowActionDialog(false);
                  setEditingActionId(null);
                  setNewAction({
                    hazard_action: "",
                    location: "",
                    priority: "B",
                    who_responsible: "",
                    date_to_rectify: getLocalDateString(),
                    closed_out_date: "",
                    closed_out_by: "",
                    status: "open",
                    images: []
                  });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog open={showCameraDialog} onOpenChange={(open) => {
        if (!open) {
          stopCamera();
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Take Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ height: "400px" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={capturePhoto} className="flex-1">
                <Camera className="w-4 h-4 mr-2" />
                Capture Photo
              </Button>
              <Button variant="outline" onClick={stopCamera} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
          {/* Hidden canvas for photo capture */}
          <canvas ref={canvasRef} className="hidden" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
